import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Wand2, ImagePlay, Repeat, Zap, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1550275994-cdc89cd1948f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const SHOT1 = "https://images.unsplash.com/photo-1579205149708-f5b24c5a04e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";
const SHOT2 = "https://images.unsplash.com/photo-1612520557101-48a83a15d1e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

const modes = [
  { icon: Wand2, title: "Text to Video", desc: "Describe a scene in words and watch it come alive in seconds.", color: "text-primary" },
  { icon: ImagePlay, title: "Image to Video", desc: "Upload any still image and animate it into a flowing cinematic clip.", color: "text-accent" },
  { icon: Repeat, title: "Video Remix", desc: "Reimagine a frame in a bold new style — anime, noir, vaporwave and more.", color: "text-primary" },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="grain">
      <section className="max-w-7xl mx-auto px-5 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-primary/10 text-primary mb-6">
            <Zap className="w-3.5 h-3.5" /> AI motion studio
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            Turn a single <span className="gradient-text">idea</span> into
            <br /> cinematic <span className="text-glow text-primary">motion</span>.
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg mt-6 max-w-lg">
            MotionCraft generates, animates and remixes video from plain text or your own images. No timeline, no render farm — just pure imagination.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button data-testid="hero-start" onClick={() => nav("/auth?mode=register")} size="lg" className="rounded-full font-semibold h-12 px-7 glow-cyan">
              Start creating <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button data-testid="hero-pricing" onClick={() => nav("/pricing")} size="lg" variant="outline" className="rounded-full font-semibold h-12 px-7 border-border">
              View plans
            </Button>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1 }}
          className="relative">
          <div className="rounded-3xl overflow-hidden glass p-2 glow-pink">
            <img src={HERO} alt="motion" className="w-full h-[380px] object-cover rounded-2xl" />
          </div>
          <div className="absolute -bottom-8 -left-6 w-40 h-56 rounded-2xl overflow-hidden glass p-1.5 hidden sm:block animate-pulse-ring">
            <img src={SHOT1} alt="frame" className="w-full h-full object-cover rounded-xl" />
          </div>
          <div className="absolute -top-6 -right-4 w-32 h-32 rounded-2xl overflow-hidden glass p-1.5 hidden sm:block">
            <img src={SHOT2} alt="frame" className="w-full h-full object-cover rounded-xl" />
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-5 pb-24">
        <div className="grid md:grid-cols-3 gap-5">
          {modes.map((m, i) => (
            <motion.div key={m.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="glass rounded-3xl p-7 hover:-translate-y-1 transition-transform">
              <div className={`w-12 h-12 rounded-2xl bg-secondary/60 grid place-items-center mb-5 ${m.color}`}>
                <m.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-2">{m.title}</h3>
              <p className="text-muted-foreground text-sm">{m.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 pb-28">
        <div className="glass rounded-[2rem] p-10 sm:p-14 text-center relative overflow-hidden">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-3">Your next scene is one prompt away.</h2>
          <p className="text-muted-foreground mb-7 max-w-md mx-auto">Join creators animating the impossible every day.</p>
          <Button data-testid="cta-bottom" onClick={() => nav("/auth?mode=register")} size="lg" className="rounded-full font-semibold h-12 px-8">
            Launch the studio
          </Button>
        </div>
      </section>
    </div>
  );
}
